/**
 * Logger Utility
 * 
 * A configurable logger that can be disabled in production.
 * Supports different log levels and can be configured via environment variables.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
    /** Enable or disable all logging */
    enabled: boolean;
    /** Minimum log level to display */
    minLevel: LogLevel;
    /** Prefix to add to all log messages */
    prefix?: string;
}

const LOG_LEVELS: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
};

// Default config based on environment
const defaultConfig: LoggerConfig = {
    enabled: import.meta.env.DEV,
    minLevel: 'debug',
    prefix: '[IGA]',
};

let currentConfig: LoggerConfig = { ...defaultConfig };

/**
 * Configure the logger at runtime.
 */
export function configureLogger(config: Partial<LoggerConfig>): void {
    currentConfig = { ...currentConfig, ...config };
}

/**
 * Check if a log level should be displayed.
 */
function shouldLog(level: LogLevel): boolean {
    if (!currentConfig.enabled) return false;
    return LOG_LEVELS[level] >= LOG_LEVELS[currentConfig.minLevel];
}

/**
 * Format the log message with prefix and timestamp.
 */
function formatMessage(level: LogLevel, message: string): string {
    const timestamp = new Date().toISOString().slice(11, 19);
    const prefix = currentConfig.prefix ? `${currentConfig.prefix} ` : '';
    return `${prefix}[${timestamp}] [${level.toUpperCase()}] ${message}`;
}

/**
 * Logger object with methods for each log level.
 */
export const logger = {
    debug(message: string, ...args: unknown[]): void {
        if (shouldLog('debug')) {
            console.log(formatMessage('debug', message), ...args);
        }
    },

    info(message: string, ...args: unknown[]): void {
        if (shouldLog('info')) {
            console.info(formatMessage('info', message), ...args);
        }
    },

    warn(message: string, ...args: unknown[]): void {
        if (shouldLog('warn')) {
            console.warn(formatMessage('warn', message), ...args);
        }
    },

    error(message: string, ...args: unknown[]): void {
        if (shouldLog('error')) {
            console.error(formatMessage('error', message), ...args);
        }
    },

    /** Log a group of related messages (only in dev mode) */
    group(label: string, fn: () => void): void {
        if (shouldLog('debug')) {
            console.group(formatMessage('debug', label));
            fn();
            console.groupEnd();
        }
    },

    /** Log a table (only in dev mode) */
    table(data: unknown): void {
        if (shouldLog('debug')) {
            console.table(data);
        }
    },
};

export default logger;
